import { BankStatementLineDocumentResponseSchema, BankStatementLineDocumentListResponseSchema, BankStatementLineDocumentCountsResponseSchema, } from '@packages/types';
import { api, apiRaw, getApiUrl, getApiBridge, parseErrorResponse, filenameFromResponse } from '../client.js';
/**
 * Upload a supporting document to a bank statement line.
 * Uses raw fetch with FormData – the api() wrapper is JSON-only.
 */
export async function uploadBankStatementLineDocument(accountId, lineId, file) {
    const bridge = getApiBridge();
    const baseUrl = getApiUrl();
    const token = bridge.getToken();
    const formData = new FormData();
    formData.append('file', file);
    const headers = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${baseUrl}/accounts/${accountId}/bank-statement-lines/${lineId}/documents`, {
        method: 'POST',
        headers,
        body: formData,
    });
    if (response.status === 401) {
        bridge.removeToken();
        bridge.onUnauthorized();
        throw new Error('Session expired');
    }
    if (!response.ok) {
        throw await parseErrorResponse(response);
    }
    const json = await response.json();
    const result = BankStatementLineDocumentResponseSchema.safeParse(json);
    if (!result.success) {
        throw new Error('Invalid response from server');
    }
    return result.data;
}
/**
 * List all documents attached to a bank statement line.
 */
export function listBankStatementLineDocuments(accountId, lineId) {
    return api(`/accounts/${accountId}/bank-statement-lines/${lineId}/documents`, BankStatementLineDocumentListResponseSchema);
}
/**
 * Delete a document from a bank statement line.
 */
export async function deleteBankStatementLineDocument(accountId, lineId, documentId) {
    await apiRaw(`/accounts/${accountId}/bank-statement-lines/${lineId}/documents/${documentId}`, { method: 'DELETE' });
}
/**
 * Fetch the document counts for a specific set of bank statement line ids.
 * Returns a sparse map: only lines (from the requested set) with at least one
 * document appear in the map. Lines with no documents are absent (treat a
 * missing key as count 0).
 *
 * The caller passes the ids of the lines currently rendered so the query stays
 * bounded by the visible page rather than growing with account history.
 */
export function getBankStatementLineDocumentCounts(accountId, lineIds) {
    return api(`/accounts/${accountId}/bank-statement-line-document-counts`, BankStatementLineDocumentCountsResponseSchema, { method: 'POST', body: { lineIds } });
}
/**
 * Constructs the authenticated download URL for a bank statement line document.
 * This URL requires a Bearer token – it cannot be used as a plain <a href>.
 * Use downloadBankStatementLineDocument() to fetch with auth and open the file.
 */
export function getBankStatementLineDocumentDownloadUrl(accountId, lineId, documentId) {
    return `${getApiUrl()}/accounts/${accountId}/bank-statement-lines/${lineId}/documents/${documentId}/download`;
}
/**
 * Fetches a document as a Blob with auth headers and opens it for preview.
 * Tries window.open first (opens in a new tab for PDFs/images), then falls
 * back to an <a download> click if the popup is blocked.
 */
export async function downloadBankStatementLineDocument(accountId, lineId, documentId) {
    const bridge = getApiBridge();
    const token = bridge.getToken();
    if (!token) {
        bridge.onUnauthorized();
        throw new Error('Session expired');
    }
    const url = getBankStatementLineDocumentDownloadUrl(accountId, lineId, documentId);
    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 401) {
        bridge.removeToken();
        bridge.onUnauthorized();
        throw new Error('Session expired');
    }
    if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    // Try opening in a new tab (best for PDF/image preview)
    const newTab = window.open(blobUrl, '_blank');
    if (!newTab) {
        // Popup blocked – fall back to <a download> click
        const filename = filenameFromResponse(response, 'document');
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
